import { Box, Typography } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';

import Button from '~/components/Button';

import {
  EActionEvent,
  EBusinessLineEvent,
  ECategoryEvent,
  useEvents,
} from '~/hooks/useEvents';
import { IPromotionCard, PromotionCardTemplate } from '~/data/types/car/cars';

import { promotionCardButtonAction } from '~/data/promotionCard';
import { useAppDispatch, useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';
import {
  setIsApprovalFlowOpen,
  setSource,
} from '~/redux-toolkit/slices/approvalFlowSlice';
import { ESourceEventOptions } from '~/data/lead/enums';
import { buttonStyle } from '../CarCard/useStyles';
import useStyles, {
  cardBackgroundStyle,
  contactUsContainerPromotionStyle,
  contactUsPromotionStyle,
  contactUsTextContainerStyle,
  discountContainerStyle,
  discountDecorationStyle,
  fundingContainerPromotionStyle,
  fundingPromotionStyle,
  fundingTextContainerStyle,
  LastWordIcon,
  leftStarContainerPromotionStyle,
  rightStarContainerPromotionStyle,
  tradeInContainerPromotionBottomStyle,
  tradeInContainerPromotionTopStyle,
  tradeInPromotionStyle,
  tradeInTextContainerStyle,
} from './useStyles';
import YellowLineTitleStyled from '~/assets/icons/jsx/yellowLineTitleStyled';
import { useIsNewCar } from '~/hooks/useIsNewCar';
import { useContactUsPopup } from '~/hooks/useContactUsPopup';
import { ROUTES } from '~/data/routes';
import { EContactUsFlow } from '~/data/ContactUsPopup/enums';
import { CONTACT_US_REDIRECTION } from '~/data/consts';
import Image from 'next/image';
import FundingPromotionCardIcon from '~/assets/icons/jsx/FundingPromotionCard';
import ContactUsPromotionCardIcon from '~/assets/icons/jsx/ContactUsPromotionCard';
import TradeInPromotionBottomCardIcon from '~/assets/icons/jsx/TradeInPromotionBottomCard';
import TradeInPromotionTopCardIcon from '~/assets/icons/jsx/TradeInPromotionTopCard';
import StarPromotionCardIcon from '~/assets/icons/jsx/StarPromotionCard';
import { useIsMobile } from '~/hooks/useIsMobile';
import PromotionCardDecore from '~/assets/images/jsx/PromotionCardDecore';

interface IPromotionCardProps {
  promotionCard: IPromotionCard;
}

const PromotionCard: FC<IPromotionCardProps> = ({ promotionCard }) => {
  const classes = useStyles();
  const { isApprovalFlowOpen } = useAppSelector(
    (state: RootState) => state.approvalFlow,
  );
  const isNewCar = useIsNewCar();
  const events = useEvents();
  const dispatch = useAppDispatch();
  const { openContactUsModal } = useContactUsPopup();
  const isMobile = useIsMobile();

  const onClickPromotionCard = {
    [promotionCardButtonAction.approval]: useCallback(() => {
      dispatch(setIsApprovalFlowOpen(!isApprovalFlowOpen));
      dispatch(setSource(ESourceEventOptions.RESULT_PAGE));
      events.sendEvent(
        {
          action: EActionEvent.RESULT_PAGE_APPROVAL_FLOW,
          category: ECategoryEvent.FUNDING_APPROVAL,
        },
        {
          leadLocation: ESourceEventOptions.PROMOTION_CARD,
          source: ESourceEventOptions.PROMOTION_CARD,
          businessLine: EBusinessLineEvent.FUNDING,
          approvalLocation: isNewCar
            ? ROUTES.RESULT_PAGE.replace('/', '')
            : ROUTES.RESULT_PAGE_USED_CAR.replace('/', ''),
        },
      );
    }, [dispatch, events, isApprovalFlowOpen, isNewCar]),
    [promotionCardButtonAction.link]: useCallback(() => {
      if (!promotionCard.button?.link) return;
      window.location.href = promotionCard.button.link;
      events.sendEvent(
        {
          action: EActionEvent.RESULT_PAGE_LINK,
          category: ECategoryEvent.RESULT_PAGE,
        },
        {
          leadLocation: ESourceEventOptions.PROMOTION_CARD,
          source: ESourceEventOptions.PROMOTION_CARD,
          pagePath: window.location.pathname,
        },
      );
    }, [events, promotionCard?.button?.link]),
    [promotionCardButtonAction.lead]: useCallback(() => {
      events.sendEvent(
        {
          action: EActionEvent.LEAD_LOAD,
          category: ECategoryEvent.RESULT_PAGE,
        },
        {
          leadLocation: ESourceEventOptions.PROMOTION_CARD,
          source: ESourceEventOptions.PROMOTION_CARD,
        },
      );

      openContactUsModal({
        flow: EContactUsFlow.LEAVE_DETAILS,
        redirectTo: CONTACT_US_REDIRECTION.HOME_PAGE,
        campaignNumber: promotionCard.regularLeadDetails?.campaignNumber,
        lineOfBusiness: promotionCard.regularLeadDetails?.lineOfBusiness,
        subMedia: promotionCard.regularLeadDetails?.subMedia,
        note: promotionCard.regularLeadDetails?.note,
        leadLocation: ESourceEventOptions.PROMOTION_CARD,
        source: ESourceEventOptions.PROMOTION_CARD,
      });
    }, [events, promotionCard, openContactUsModal]),
    [promotionCardButtonAction.tradeInLead]: useCallback(() => {
      events.sendEvent(
        {
          action: EActionEvent.LEAD_LOAD,
          category: ECategoryEvent.RESULT_PAGE,
        },
        {
          leadLocation: ESourceEventOptions.PROMOTION_CARD,
          source: ECategoryEvent.TRADE_IN_OFFER,
        },
      );
      openContactUsModal({
        flow: EContactUsFlow.LEAVE_DETAILS,
        campaignNumber: promotionCard.regularLeadDetails?.campaignNumber,
        lineOfBusiness: promotionCard.regularLeadDetails?.lineOfBusiness,
        subMedia: promotionCard.regularLeadDetails?.subMedia,
        note: promotionCard.regularLeadDetails?.note || '',
        showNote: true,
        leadTitle: promotionCard.regularLeadDetails?.title || '',
        leadLocation: ESourceEventOptions.USED_CAR_PAGE,
      });
    }, [openContactUsModal, events, promotionCard]),
    [promotionCardButtonAction.brandsLead]: useCallback(() => {
      events.sendEvent(
        {
          action: EActionEvent.LEAD_LOAD,
          category: ECategoryEvent.RESULT_PAGE,
        },
        {
          leadLocation: ESourceEventOptions.PROMOTION_CARD,
          source: ESourceEventOptions.PROMOTION_CARD,
        },
      );

      openContactUsModal({
        flow: EContactUsFlow.LEAVE_DETAILS,
        redirectTo: CONTACT_US_REDIRECTION.HOME_PAGE,
        isBrandsLead: true,
        brandsLeadDataDropdownOptions: promotionCard.brandsLeadDropdownOptions,
        leadLocation: ESourceEventOptions.PROMOTION_CARD,
        source: ESourceEventOptions.PROMOTION_CARD,
      });
    }, [events, openContactUsModal, promotionCard.brandsLeadDropdownOptions]),
  };

  const formatTextWithLineBreaks = (text: string) => {
    if (!text.includes('\n') && !text.includes('\\n')) {
      return text;
    }

    return text
      .replaceAll('\\n', '\n')
      .split('\n')
      .map((line, index, array) => (
        <span key={index}>
          {line}
          {index < array.length - 1 && <br />}
        </span>
      ));
  };

  const title = useMemo(() => {
    const textArr = promotionCard?.title?.split(' ');
    return {
      first: textArr.slice(0, textArr.length - 1).join(' ') || '',
      last: ` ${textArr[textArr.length - 1]}` || '',
    };
  }, [promotionCard?.title]);

  const titleImage = useMemo(() => {
    const imageData = promotionCard.titleImage?.data?.attributes;
    if (imageData && Object.keys(imageData).length > 0) {
      return imageData;
    }

    return null;
  }, [promotionCard?.titleImage]);

  const contentContainerTemplateStyle = useMemo(() => {
    switch (promotionCard.template) {
      case PromotionCardTemplate.CONTACT_US:
        return contactUsTextContainerStyle;
      case PromotionCardTemplate.TRADE_IN:
        return tradeInTextContainerStyle;
      case PromotionCardTemplate.FUNDING:
        return fundingTextContainerStyle;
      case PromotionCardTemplate.DISCOUNT:
        return discountContainerStyle;
      default:
        return {};
    }
  }, [promotionCard.template]);

  const backgroundImage = useMemo(() => {
    if (!promotionCard.preDesignedBanner) {
      return null;
    }
    return isMobile
      ? promotionCard?.mobileImage?.data?.attributes?.url
      : promotionCard?.desktopImage?.data?.attributes?.url;
  }, [promotionCard, isMobile]);

  if (promotionCard.template === PromotionCardTemplate.DISCOUNT) {
    return (
      <Box sx={classes.root}>
        <Box
          sx={{
            ...classes.cardContainer,
            ...classes.promotionCardContainer,
          }}
        >
          <Typography
            variant="h3"
            fontWeight={400}
            fontSize="42px"
            lineHeight="1"
            mb={1}
          >
            {formatTextWithLineBreaks(promotionCard.title)}
          </Typography>

          {promotionCard.description && (
            <Typography fontWeight="bold" fontSize="42px" lineHeight="1">
              {formatTextWithLineBreaks(promotionCard.description)}
            </Typography>
          )}
          <PromotionCardDecore Component={discountDecorationStyle} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={classes.root}>
      {promotionCard.preDesignedBanner && backgroundImage && (
        <Image
          style={cardBackgroundStyle}
          src={backgroundImage}
          alt="background"
          width={600}
          height={500}
        />
      )}

      {!backgroundImage && (
        <>
          {promotionCard.template === PromotionCardTemplate.GENERIC && (
            <>
              <Box sx={classes.rightLinePromotionStyle} />
              <Box sx={classes.leftLinePromotionStyle} />
            </>
          )}
          {promotionCard.template === PromotionCardTemplate.FUNDING && (
            <Box sx={fundingContainerPromotionStyle}>
              <FundingPromotionCardIcon Component={fundingPromotionStyle} />
            </Box>
          )}
          {promotionCard.template === PromotionCardTemplate.CONTACT_US && (
            <>
              <Box sx={rightStarContainerPromotionStyle}>
                <StarPromotionCardIcon Component={tradeInPromotionStyle} />
              </Box>
              <Box sx={contactUsContainerPromotionStyle}>
                <ContactUsPromotionCardIcon
                  Component={contactUsPromotionStyle}
                />
              </Box>
            </>
          )}
          {promotionCard.template === PromotionCardTemplate.TRADE_IN && (
            <>
              <Box sx={leftStarContainerPromotionStyle}>
                <StarPromotionCardIcon Component={tradeInPromotionStyle} />
              </Box>
              <Box sx={tradeInContainerPromotionTopStyle}>
                <TradeInPromotionTopCardIcon
                  Component={tradeInPromotionStyle}
                />
              </Box>
              <Box sx={tradeInContainerPromotionBottomStyle}>
                <TradeInPromotionBottomCardIcon
                  Component={tradeInPromotionStyle}
                />
              </Box>
            </>
          )}
        </>
      )}
      <Box sx={classes.cardContainer}>
        <Box
          sx={{ ...classes.contentContainer, ...contentContainerTemplateStyle }}
        >
          {/* Start */}

          {!backgroundImage && (
            <>
              {!titleImage ? (
                <>
                  <Typography variant="h3" sx={classes.titleStyle}>
                    {formatTextWithLineBreaks(title.first)}{' '}
                    <Typography sx={classes.lastWordTitle}>
                      {title.last}
                      {promotionCard.template ===
                        PromotionCardTemplate.GENERIC && (
                        <YellowLineTitleStyled Component={LastWordIcon} />
                      )}
                    </Typography>
                  </Typography>
                  {promotionCard.description?.length > 0 && (
                    <Box sx={classes.descriptionContainer}>
                      <Typography fontSize={'42px'} lineHeight={'1'}>
                        {formatTextWithLineBreaks(promotionCard.description)}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Image
                    src={titleImage.url}
                    alt={titleImage.alternativeText}
                    width={titleImage.width || 320}
                    height={titleImage.height || 55}
                  />
                  <Typography variant="h3" fontSize={'42px'} lineHeight={'1'}>
                    {formatTextWithLineBreaks(promotionCard.title)}
                  </Typography>
                  {promotionCard.description?.length > 0 && (
                    <Box sx={classes.descriptionContainerSec}>
                      <Typography>
                        {formatTextWithLineBreaks(promotionCard.description)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* End */}
        </Box>

        {promotionCard.showButton && (
          <Box sx={classes.buttonContainer}>
            <Button
              label={promotionCard?.button?.label}
              customStyle={buttonStyle}
              type={BUTTON_TYPES.MAIN_YELLOW}
              size={BUTTON_SIZES.LARGE}
              onClickFunc={
                onClickPromotionCard[promotionCard?.button?.buttonAction]
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default PromotionCard;
